import { useState, useEffect } from 'react';

const CodeEditorSettings = ({ isOpen, onClose, settings, onSettingsChange, onResetSettings }) => {
  const [activeTab, setActiveTab] = useState('appearance');
  const [currentSettings, setCurrentSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Initialize current settings when modal opens or settings change
  useEffect(() => {
    if (isOpen && settings) {
      setCurrentSettings(settings);
      setHasChanges(false);
      setSaveMessage('');
    }
  }, [isOpen, settings]);

  if (!isOpen) return null;

  // Default settings structure
  const defaultSettings = {
    outputPlacement: 'bottom',
    // Appearance & Theme
    theme: 'vs-dark',
    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    fontSize: 14,
    fontLigatures: true,
    lineHeight: 1.5,
    letterSpacing: 0,
    minimapEnabled: false,
    minimapRenderCharacters: true,
    minimapMaxColumn: 120,
    renderWhitespace: 'none',
    renderControlCharacters: false,
    cursorStyle: 'line',
    cursorBlinking: 'blink',
    // Editor Behavior
    wordWrap: 'on',
    wordWrapColumn: 80,
    lineNumbers: 'on',
    tabSize: 4,
    insertSpaces: true,
    autoClosingBrackets: 'always',
    autoClosingQuotes: 'always',
    autoClosingDelete: 'auto',
    autoIndent: 'full',
    folding: true,
    foldingHighlight: true,
    codeLens: true,
    quickSuggestions: true,
    parameterHintsEnabled: true,
    acceptSuggestionOnEnter: 'on',
    // Layout & View
    rulers: [],
    scrollBeyondLastLine: false,
    smoothScrolling: true,
    stickyScrollEnabled: false,
    ...currentSettings
  };

  const handleSettingChange = (key, value) => {
    const newSettings = {
      ...currentSettings,
      [key]: value
    };
    setCurrentSettings(newSettings);
    setHasChanges(true);
    setSaveMessage(''); // Clear any previous save message
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Call the parent's onSettingsChange which handles the backend save
      await onSettingsChange(currentSettings);
      setHasChanges(false);
      setSaveMessage('Settings saved successfully!');

      // Auto-close after successful save with a delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to save settings:', error);
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      if (onResetSettings) {
        await onResetSettings();
        setHasChanges(false);
        setSaveMessage('Settings reset to defaults successfully!');

        // Auto-close after successful reset with a delay
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Failed to reset settings:', error);
      setSaveMessage('Failed to reset settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  const tabs = [
    { id: 'appearance', label: '🎨 Appearance', icon: '🎨' },
    { id: 'behavior', label: '⚙️ Behavior', icon: '⚙️' },
    { id: 'layout', label: '📏 Layout', icon: '📏' }
  ];

  const themes = [
    { value: 'vs', label: 'Light (VS)' },
    { value: 'vs-dark', label: 'Dark (VS Dark)' },
    { value: 'hc-black', label: 'High Contrast Black' }
  ];

  const fontFamilies = [
    'Monaco, Menlo, "Ubuntu Mono", monospace',
    '"Fira Code", monospace',
    '"JetBrains Mono", monospace',
    '"Cascadia Code", monospace',
    '"Source Code Pro", monospace',
    'Consolas, monospace'
  ];

  const renderAppearanceTab = () => (
    <div className="space-y-6">
      {/* Theme Selection */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Theme</h4>
        <select
          value={defaultSettings.theme}
          onChange={(e) => handleSettingChange('theme', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {themes.map(theme => (
            <option key={theme.value} value={theme.value}>{theme.label}</option>
          ))}
        </select>
      </div>

      {/* Font Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Font</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Family</label>
            <select
              value={defaultSettings.fontFamily}
              onChange={(e) => handleSettingChange('fontFamily', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {fontFamilies.map(font => (
                <option key={font} value={font}>{font.split(',')[0].replace(/"/g, '')}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Font Size</label>
            <input
              type="number"
              min="10"
              max="24"
              value={defaultSettings.fontSize}
              onChange={(e) => handleSettingChange('fontSize', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Line Height</label>
            <input
              type="number"
              min="1"
              max="3"
              step="0.1"
              value={defaultSettings.lineHeight}
              onChange={(e) => handleSettingChange('lineHeight', parseFloat(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Letter Spacing</label>
            <input
              type="number"
              min="-2"
              max="5"
              step="0.1"
              value={defaultSettings.letterSpacing}
              onChange={(e) => handleSettingChange('letterSpacing', parseFloat(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.fontLigatures}
              onChange={(e) => handleSettingChange('fontLigatures', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Enable font ligatures</span>
          </label>
        </div>
      </div>

      {/* Minimap */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Minimap</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.minimapEnabled}
              onChange={(e) => handleSettingChange('minimapEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Show minimap</span>
          </label>
          {defaultSettings.minimapEnabled && (
            <div className="ml-6 space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={defaultSettings.minimapRenderCharacters}
                  onChange={(e) => handleSettingChange('minimapRenderCharacters', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600">Render characters</span>
              </label>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Max Column</label>
                <input
                  type="number"
                  min="80"
                  max="200"
                  value={defaultSettings.minimapMaxColumn}
                  onChange={(e) => handleSettingChange('minimapMaxColumn', parseInt(e.target.value))}
                  className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cursor & Visual */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Cursor & Visual</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cursor Style</label>
            <select
              value={defaultSettings.cursorStyle}
              onChange={(e) => handleSettingChange('cursorStyle', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="line">Line</option>
              <option value="block">Block</option>
              <option value="underline">Underline</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Cursor Blinking</label>
            <select
              value={defaultSettings.cursorBlinking}
              onChange={(e) => handleSettingChange('cursorBlinking', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="blink">Blink</option>
              <option value="smooth">Smooth</option>
              <option value="phase">Phase</option>
              <option value="expand">Expand</option>
              <option value="solid">Solid</option>
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs text-gray-600 mb-1">Render Whitespace</label>
          <select
            value={defaultSettings.renderWhitespace}
            onChange={(e) => handleSettingChange('renderWhitespace', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="none">None</option>
            <option value="boundary">Boundary</option>
            <option value="selection">Selection</option>
            <option value="all">All</option>
          </select>
        </div>
        <div className="mt-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.renderControlCharacters}
              onChange={(e) => handleSettingChange('renderControlCharacters', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Render control characters</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderBehaviorTab = () => (
    <div className="space-y-6">
      {/* Word Wrap */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Word Wrap</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <select
              value={defaultSettings.wordWrap}
              onChange={(e) => handleSettingChange('wordWrap', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="off">Off</option>
              <option value="on">On</option>
              <option value="wordWrapColumn">At Column</option>
              <option value="bounded">Bounded</option>
            </select>
          </div>
          {defaultSettings.wordWrap === 'wordWrapColumn' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Wrap Column</label>
              <input
                type="number"
                min="40"
                max="200"
                value={defaultSettings.wordWrapColumn}
                onChange={(e) => handleSettingChange('wordWrapColumn', parseInt(e.target.value))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Line Numbers & Tabs */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Line Numbers & Tabs</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Line Numbers</label>
            <select
              value={defaultSettings.lineNumbers}
              onChange={(e) => handleSettingChange('lineNumbers', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="on">On</option>
              <option value="off">Off</option>
              <option value="relative">Relative</option>
              <option value="interval">Interval</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Tab Size</label>
            <input
              type="number"
              min="1"
              max="8"
              value={defaultSettings.tabSize}
              onChange={(e) => handleSettingChange('tabSize', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={defaultSettings.insertSpaces}
                onChange={(e) => handleSettingChange('insertSpaces', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Insert spaces</span>
            </label>
          </div>
        </div>
      </div>

      {/* Auto Closing */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Auto Closing</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Brackets</label>
            <select
              value={defaultSettings.autoClosingBrackets}
              onChange={(e) => handleSettingChange('autoClosingBrackets', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="always">Always</option>
              <option value="languageDefined">Language Defined</option>
              <option value="beforeWhitespace">Before Whitespace</option>
              <option value="never">Never</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quotes</label>
            <select
              value={defaultSettings.autoClosingQuotes}
              onChange={(e) => handleSettingChange('autoClosingQuotes', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="always">Always</option>
              <option value="languageDefined">Language Defined</option>
              <option value="beforeWhitespace">Before Whitespace</option>
              <option value="never">Never</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Delete</label>
            <select
              value={defaultSettings.autoClosingDelete}
              onChange={(e) => handleSettingChange('autoClosingDelete', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="auto">Auto</option>
              <option value="always">Always</option>
              <option value="never">Never</option>
            </select>
          </div>
        </div>
      </div>

      {/* Auto Indent & Folding */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Auto Indent & Folding</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Auto Indent</label>
            <select
              value={defaultSettings.autoIndent}
              onChange={(e) => handleSettingChange('autoIndent', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="none">None</option>
              <option value="keep">Keep</option>
              <option value="brackets">Brackets</option>
              <option value="advanced">Advanced</option>
              <option value="full">Full</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={defaultSettings.folding}
                onChange={(e) => handleSettingChange('folding', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Enable folding</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={defaultSettings.foldingHighlight}
                onChange={(e) => handleSettingChange('foldingHighlight', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Folding highlight</span>
            </label>
          </div>
        </div>
      </div>

      {/* IntelliSense */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">IntelliSense & Suggestions</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.codeLens}
              onChange={(e) => handleSettingChange('codeLens', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Enable CodeLens</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.quickSuggestions}
              onChange={(e) => handleSettingChange('quickSuggestions', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Quick suggestions</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.parameterHintsEnabled}
              onChange={(e) => handleSettingChange('parameterHintsEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Parameter hints</span>
          </label>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Accept Suggestion on Enter</label>
            <select
              value={defaultSettings.acceptSuggestionOnEnter}
              onChange={(e) => handleSettingChange('acceptSuggestionOnEnter', e.target.value)}
              className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="on">On</option>
              <option value="smart">Smart</option>
              <option value="off">Off</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLayoutTab = () => (
    <div className="space-y-6">
      {/* Output Pane Placement */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Output Pane Placement</h4>
        <div className="space-y-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="outputPlacement"
              value="bottom"
              checked={defaultSettings.outputPlacement === 'bottom'}
              onChange={(e) => handleSettingChange('outputPlacement', e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Below Editor</div>
              <div className="text-sm text-gray-500">Output appears below the code editor</div>
            </div>
            <div className="w-12 h-8 border-2 border-gray-300 rounded flex flex-col">
              <div className="flex-1 bg-blue-100 rounded-t"></div>
              <div className="h-2 bg-green-100 rounded-b"></div>
            </div>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="radio"
              name="outputPlacement"
              value="right"
              checked={defaultSettings.outputPlacement === 'right'}
              onChange={(e) => handleSettingChange('outputPlacement', e.target.value)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Right Side</div>
              <div className="text-sm text-gray-500">Output appears to the right of the code editor</div>
            </div>
            <div className="w-12 h-8 border-2 border-gray-300 rounded flex">
              <div className="flex-1 bg-blue-100 rounded-l"></div>
              <div className="w-3 bg-green-100 rounded-r"></div>
            </div>
          </label>
        </div>
      </div>

      {/* AI Feedback Information */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">AI Feedback Placement</h4>
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-blue-900 mb-1">Smart Auto-Placement</h5>
              <p className="text-sm text-blue-700">
                AI Feedback automatically appears opposite to your Output pane:
              </p>
              <ul className="mt-2 text-sm text-blue-600 list-disc list-inside space-y-1">
                <li>When Output is <strong>Bottom</strong> → AI Feedback appears on <strong>Right</strong></li>
                <li>When Output is <strong>Right</strong> → AI Feedback appears on <strong>Bottom</strong></li>
              </ul>
              <p className="text-xs text-blue-600 mt-2">
                This ensures maximum screen space for your code while keeping both panels visible.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Rulers */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Column Rulers</h4>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Ruler positions (comma-separated, e.g., "80,120")</label>
          <input
            type="text"
            placeholder="80, 120"
            value={defaultSettings.rulers.join(', ')}
            onChange={(e) => {
              const rulers = e.target.value
                .split(',')
                .map(r => parseInt(r.trim()))
                .filter(r => !isNaN(r) && r > 0);
              handleSettingChange('rulers', rulers);
            }}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Scrolling Options */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 mb-3">Scrolling</h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.scrollBeyondLastLine}
              onChange={(e) => handleSettingChange('scrollBeyondLastLine', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Scroll beyond last line</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.smoothScrolling}
              onChange={(e) => handleSettingChange('smoothScrolling', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Smooth scrolling</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={defaultSettings.stickyScrollEnabled}
              onChange={(e) => handleSettingChange('stickyScrollEnabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">Sticky scroll (keep headers visible)</span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'appearance':
        return renderAppearanceTab();
      case 'behavior':
        return renderBehaviorTab();
      case 'layout':
        return renderLayoutTab();
      default:
        return renderAppearanceTab();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Settings Modal - Larger size */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-800">⚙️ Code Editor Settings</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {renderActiveTab()}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            {/* Save Message */}
            {saveMessage && (
              <div className={`mb-3 p-2 rounded-md text-sm ${
                saveMessage.includes('successfully')
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`}>
                {saveMessage}
              </div>
            )}

            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {hasChanges ? (
                  <span className="text-orange-600">⚠️ You have unsaved changes</span>
                ) : (
                  <span>Settings synced with your account</span>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleReset}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </span>
                  ) : (
                    'Reset to Defaults'
                  )}
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving || !hasChanges}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    hasChanges
                      ? 'text-white bg-blue-600 border border-transparent hover:bg-blue-700'
                      : 'text-gray-700 bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isSaving ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : hasChanges ? (
                    'Save Settings'
                  ) : (
                    'No Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CodeEditorSettings;